import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
	AlertTriangle,
	Check,
	Eye,
	File,
	FileText,
	Plus,
	RefreshCw,
	RotateCcw,
	Search,
	Trash2,
	X,
} from 'lucide-react';
import { useToast } from 'src/context/ToastContext';

import {
	type DocumentCheck,
	type StatementFile,
	type StatementListParams,
	useDeleteStatementMutation,
	useGetEnterpriseUsersQuery,
	useGetStatementsQuery,
	useRetryStatementMutation,
} from '../api';
import { useLazyGetMerchantRecordQuery } from '../api/merchantRecords';
import { useSubscription } from '../hooks';
import { useConfigs } from '../hooks/useConfigs';
import { useAuth } from '../providers/AppAuthProvider';
import {
	groupStatus,
	recordRowStatus,
	statementsLabel,
} from '../utils/merchantRecord';
import {
	formatRatePlanType,
	formatRatePlanTypes,
	statementMonth,
} from '../utils/utils';

import {
	Alert,
	Button,
	FilterDropdown,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	PageContent,
	PageHeader,
	SkeletonTable,
	Typography,
} from './common';
import ConfigSetupModal from './ConfigSetupModal';
import CustomizeFeeStructureModal from './CustomizeFeeStructureModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import NoConfigModal from './NoConfigModal';
import StatementUpload from './StatementUpload';
import {
	countByGroup,
	matchesChip,
	STATUS_CHIPS,
	type StatementStatus,
	statementStatus,
	type StatusGroup,
} from './statementStatus';

import type { FilterGroup, FilterOption } from './common';

// Transform StatementFile to Document interface for backward compatibility
interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
	rate_plan_type?: string;
	selected_rate_types?: string[];
	my_revenue?: number | null;
	document_check?: DocumentCheck | null;
	needs_new_file?: boolean;
	partner?: { id: string; name: string } | null;
	v9Status: StatementStatus;
	merchantRecordId?: string | null;
	merchantName?: string | null;
	/** "3 statements · Aug–Oct 2026" on the one row that stands for a merchant record. */
	groupLabel?: string;
}

const GROUP_STATUS_LABEL = {
	processing: 'processing',
	completed: 'completed',
	partial: 'Needs attention',
	failed: 'failed',
} as const;

// shortcut: groups months only within the loaded page; months uploaded together sit
// next to each other in upload order, so a page split is rare. Group server-side if it bites.
const groupMerchantRecords = (docs: Document[]): Document[] => {
	const groups = new Map<string, Document[]>();
	docs.forEach((doc) => {
		if (doc.merchantRecordId)
			groups.set(doc.merchantRecordId, [
				...(groups.get(doc.merchantRecordId) ?? []),
				doc,
			]);
	});
	return docs.flatMap((doc) => {
		const months = doc.merchantRecordId && groups.get(doc.merchantRecordId);
		if (!months) return [doc];
		if (months[0] !== doc) return [];
		return [
			{
				...doc,
				name: doc.merchantName || doc.name,
				status: GROUP_STATUS_LABEL[groupStatus(months.map((m) => m.status))],
				v9Status: recordRowStatus(
					months.map((m) => ({
						status: m.status,
						document_check: m.document_check,
					})),
				),
				groupLabel: statementsLabel(
					months.length,
					months.map((m) => m.processingMonth),
				),
			},
		];
	});
};

const documentPath = (doc: Document) =>
	doc.groupLabel
		? `/merchant-records/${doc.merchantRecordId}`
		: `/analysis/${doc.id}`;


// v9 `reports` styles (Basys-prototype-v9.html: thead th, tbody td, .btn-secondary, .btn-sm, .input).
const TH =
	'bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500 border-b border-gray-200 whitespace-nowrap';
const TD = 'px-6 py-4 align-middle text-sm text-gray-900';
const SECONDARY =
	'!border-gray-300 bg-white !text-primary-700 hover:!bg-gray-50';
const BTN_SM =
	'inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium leading-[18px] text-primary-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const INPUT =
	'block rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const CHIP_STYLE: Record<StatementStatus['tone'], string> = {
	stop: 'bg-red-50 text-red-700 border-red-100',
	hard: 'bg-sand-100 text-sand-900 border-sand-300',
	soft: 'bg-sand-50 text-sand-900 border-sand-300',
	proc: 'bg-primary-50 text-primary-700 border-primary-100',
	ok: 'bg-green-100 text-green-800 border-green-200',
};
const CHIP_ICON: Record<StatementStatus['tone'], typeof Check> = {
	stop: X,
	hard: AlertTriangle,
	soft: AlertTriangle,
	proc: RefreshCw,
	ok: Check,
};

const StatusChip: React.FC<{ status: StatementStatus }> = ({ status }) => {
	const Icon = CHIP_ICON[status.tone];
	return (
		<span
			className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-1 text-xs font-medium leading-[1.3] ${CHIP_STYLE[status.tone]}`}
			title={status.warning}
		>
			<Icon className="h-3 w-3" />
			{status.label}
		</span>
	);
};

const transformToDocument = (statement: StatementFile): Document => {
	return {
		id: statement.id,
		name: statement.original_filename,
		type: statement.analysis_type,
		uploaded_by: statement.uploaded_by,
		date: new Date(statement.created_at).toISOString().split('T')[0],
		size: `${(statement.file_size / 1024).toFixed(1)} KB`,
		status:
			statement.current_status === 'processed'
				? 'completed'
				: statement.current_status === 'processing'
					? 'processing'
					: statement.current_status === 'error'
						? 'failed'
						: statement.current_status,
		processingMonth: statementMonth(statement),
		pdfUrl: statement.results?.signed_urls?.pdf_url,
		results: statement.results,
		rate_plan_type: statement.rate_plan_type,
		selected_rate_types: statement.selected_rate_types,
		my_revenue: statement.revenue_analysis?.total_revenue ?? null,
		document_check: statement.document_check ?? null,
		needs_new_file: statement.needs_new_file ?? false,
		partner: statement.partner ?? null,
		v9Status: statementStatus(statement),
		merchantRecordId: statement.merchant_record_id,
		merchantName: statement.merchant_name,
	};
};

interface StatementAnalyzerProps {
	onDocumentProcessed?: (documentId: string) => void;
	uploadConfig?: any;
	onFirstTimeUserUpload?: (documentId: string) => void;
	uploadedDocumentId?: string;
	onGoBackToList?: () => void;
	onUpgradePlan?: () => void;
	pageTitle?: string;
	pageSubtitle?: string;
}

const StatementAnalyzer: React.FC<StatementAnalyzerProps> = ({
	pageTitle,
	pageSubtitle,
}) => {
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showRetryModal, setShowRetryModal] = useState(false);
	const [retryDocument, setRetryDocument] = useState<Document | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
		null,
	);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [showCustomizeModal, setShowCustomizeModal] = useState(false);
	const [customizeDocument, setCustomizeDocument] = useState<Document | null>(
		null,
	);
	const [customizeConfigId, setCustomizeConfigId] = useState<string>('');

	// Filtering state
	const [filters, setFilters] = useState<StatementListParams>({
		uploaded_by: '',
		search: '',
		profit_status: '',
		// No engine filter by default: list every statement for the tenant
		// (both legacy `winstar` and `platform` engines). Forcing
		// engine='platform' here hid pre-migration / restored statements.
		page: 1,
		page_size: 50,
	});

	const [searchInput, setSearchInput] = useState('');
	const [chip, setChip] = useState<StatusGroup | 'all'>('all');
	const [partnerId, setPartnerId] = useState('');
	const [selectedUploaders, setSelectedUploaders] = useState<string[]>([]);
	const [selectedProfitStatus, setSelectedProfitStatus] = useState<string[]>(
		[],
	);

	const navigate = useNavigate();
	const { user, isAdmin } = useAuth();

	// Fetched once and refreshed on demand via the Refresh button (and after
	// upload / delete / customize). We intentionally do NOT auto-poll — the
	// previous 10s poll reloaded the table repeatedly whenever anything was
	// processing, which was disruptive. Click Refresh to pull the latest status.
	const {
		data: statements,
		isLoading,
		refetch,
		isFetching,
	} = useGetStatementsQuery(filters);
	const { data: enterpriseUsers, isLoading: isLoadingUsers } =
		useGetEnterpriseUsersQuery(undefined, {
			skip: !isAdmin, // Only fetch enterprise users for admin users
		});
	const [deleteStatementMutation, { isLoading: isDeleting }] =
		useDeleteStatementMutation();
	const [fetchMerchantRecord] = useLazyGetMerchantRecordQuery();

	// Get subscription data and derived values
	const { subscriptionStatus, refetch: refetchSubscription } =
		useSubscription();

	const canDeleteDocument = (doc: Document) => {
		return doc.uploaded_by?.email === user?.email;
	};

	// Handle delete confirmation
	const handleDeleteClick = (doc: Document) => {
		setDocumentToDelete(doc);
		setDeleteError(null);
		setShowDeleteModal(true);
	};

	const handleDeleteConfirm = async () => {
		if (!documentToDelete) return;

		setDeleteError(null);
		try {
			// The API deletes the record with its last month.
			// The list groups the loaded page only, so ask the record for all its months.
			const ids = documentToDelete.groupLabel
				? (
						await fetchMerchantRecord(
							documentToDelete.merchantRecordId!,
						).unwrap()
					).months.map((m) => m.file_id)
				: [documentToDelete.id];
			for (const id of ids) {
				await deleteStatementMutation(id).unwrap();
			}
			refetch(); // Refresh the list
			setShowDeleteModal(false);
			setDocumentToDelete(null);
		} catch (error) {
			console.error('Failed to delete statement:', error);
			setDeleteError(
				(error as any)?.data?.message ||
					'Could not delete this document right now. Please try again in a moment.',
			);
		}
	};

	const handleDeleteCancel = () => {
		setShowDeleteModal(false);
		setDocumentToDelete(null);
		setDeleteError(null);
	};

	const handleCustomizeClick = (doc: Document) => {
		setCustomizeDocument(doc);
		const configIdFromDoc = (doc as any)?.config_id || configs?.[0]?.id || '';
		setCustomizeConfigId(configIdFromDoc);
		setShowCustomizeModal(true);
	};

	const handleCustomizeClose = () => {
		setShowCustomizeModal(false);
		setCustomizeDocument(null);
		setCustomizeConfigId('');
	};

	const handleCustomizeConfirm = () => {
		refetch();
		refreshConfigs();
	};

	// Get configs from store
	const {
		refreshConfigs,
		checkHasConfigs,
		isLoading: configsLoading,
	} = useConfigs();

	// Transform statements to documents
	const allDocuments = useMemo(
		() => groupMerchantRecords(statements?.files.map(transformToDocument) ?? []),
		[statements],
	);
	// shortcut: chips, counts and the partner filter cover the loaded page only; move them to the API when lists pass one page.
	const partners = useMemo(
		() => [
			...new Map(
				allDocuments
					.filter((d) => d.partner)
					.map((d) => [d.partner!.id, d.partner!.name]),
			),
		],
		[allDocuments],
	);
	const partnerDocuments = partnerId
		? allDocuments.filter((d) => d.partner?.id === partnerId)
		: allDocuments;
	const chipCounts = countByGroup(partnerDocuments.map((d) => d.v9Status));
	const documents = partnerDocuments.filter((d) =>
		matchesChip(d.v9Status, chip),
	);

	// Filter handlers
	const handleFilterChange = (
		key: keyof StatementListParams,
		value: string | number,
	) => {
		setFilters((prev) => ({
			...prev,
			[key]: value,
			page: 1, // Reset to first page when filters change
		}));
	};

	const debouncedSearch = useCallback((searchValue: string) => {
		setFilters((prev) => ({
			...prev,
			search: searchValue,
			page: 1,
		}));
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			debouncedSearch(searchInput);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput, debouncedSearch]);

	const handleSearchChange = (search: string) => {
		setSearchInput(search);
	};

	const clearAllFilters = () => {
		setFilters({
			uploaded_by: '',
			search: '',
			profit_status: '',
			page: 1,
			page_size: 50,
		});
		setSearchInput('');
		setSelectedUploaders([]);
		setSelectedProfitStatus([]);
	};

	// Get unique uploaded by values from enterprise users (admin only)
	const uploaderOptions: FilterOption[] = enterpriseUsers?.users
		? enterpriseUsers.users.map((user) => ({
				id: user.email,
				label: user.name,
				value: user.email,
			}))
		: [];

	// Profit filter options
	const profitOptions: FilterOption[] = [
		{ id: 'positive', label: 'Positive', value: 'positive' },
		{ id: 'negative', label: 'Negative', value: 'negative' },
	];

	// Handle filter selection change
	const handleUploaderFilterChange = (values: string[]) => {
		setSelectedUploaders(values);
		handleFilterChange('uploaded_by', values.join(','));
	};

	// Handle profit filter change (single select - use first value or empty)
	const handleProfitFilterChange = (values: string[]) => {
		setSelectedProfitStatus(values);
		// Only use the first selected value (single select behavior)
		const profitValue = values.length > 0 ? values[values.length - 1] : '';
		// If both selected, clear (show all)
		const finalValue = values.length === 2 ? '' : profitValue;
		setSelectedProfitStatus(finalValue ? [finalValue] : []);
		handleFilterChange('profit_status', finalValue);
	};

	const handlePageChange = (page: number) => {
		setFilters((prev) => ({
			...prev,
			page,
		}));
	};

	// Effect to refetch subscription status when component mounts
	useEffect(() => {
		if (refetchSubscription) {
			refetchSubscription();
		}
	}, [refetchSubscription]);

	// Effect to refresh configs when component mounts
	useEffect(() => {
		refreshConfigs();
	}, []);

	// Effect to sync selectedUploaders with filters.uploaded_by
	useEffect(() => {
		if (filters.uploaded_by) {
			const emails = filters.uploaded_by.split(',').filter(Boolean);
			setSelectedUploaders(emails);
		} else {
			setSelectedUploaders([]);
		}
	}, [filters.uploaded_by]);

	const [deleteStatement, { isLoading: deleting }] =
		useDeleteStatementMutation();
	const [retryStatement, { isLoading: retrying }] = useRetryStatementMutation();
	const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
	const [showConfigSetupModal, setShowConfigSetupModal] = useState(false);
	const [showNoConfigModal, setShowNoConfigModal] = useState(false);

	const { showToast } = useToast();

	const handleUploadClick = async () => {
		// Refetch subscription status to get latest credits
		if (refetchSubscription) {
			refetchSubscription();
		}

		// Check upload permission from subscription status API
		const canUpload = subscriptionStatus?.can_upload;
		const creditsRemaining = subscriptionStatus?.credits_remaining;

		// The config list may still be in flight (e.g. right after returning from
		// an analysis page); an empty store then is "unknown", not "no configs".
		const userHasConfigs = checkHasConfigs() || configsLoading;

		const hasNoCredits =
			subscriptionStatus && subscriptionStatus.credits_remaining === 0;

		// Check if user has no credits first
		if (hasNoCredits) {
			setShowNoCreditsModal(true);
			return;
		}

		// For first-time users (credits = -1), allow upload without configs
		if (creditsRemaining === -1) {
			return;
		}

		// Allow upload if:
		// 1. can_upload is true AND
		// 2. credits > 0 (has credits) AND
		// 3. user has at least one configuration (for returning users)
		if (
			canUpload &&
			typeof creditsRemaining === 'number' &&
			creditsRemaining > 0 &&
			userHasConfigs
		) {
			setShowUploadModal(true);
		} else if (!userHasConfigs) {
			// Show NoConfigModal if user has no configurations (only for returning users)
			setShowNoConfigModal(true);
		} else {
			// Show config setup modal if no credits or upload not allowed
			setShowConfigSetupModal(true);
		}
	};

	// Handle navigation to subscription page
	const handleGoToSubscription = () => {
		setShowNoCreditsModal(false);
		navigate('/subscription');
	};

	const handleDelete = async (doc: Document) => {
		if (window.confirm(`Are you sure you want to delete "${doc.name}"?`)) {
			try {
				await deleteStatement(doc.id).unwrap();
				showToast(
					'File Deleted',
					`"${doc.name}" has been successfully deleted.`,
					'success',
				);
			} catch (error: any) {
				console.error('Failed to delete file:', error);
				const errorMessage =
					error?.data?.message ||
					error?.message ||
					'Failed to delete file. Please try again.';
				showToast('Delete Failed', errorMessage, 'error');
			}
		}
	};

	const handleRetry = async (doc: Document) => {
		setRetryDocument(doc);
		setShowRetryModal(true);
	};

	const confirmRetry = async () => {
		if (!retryDocument) return;

		try {
			await retryStatement(retryDocument.id).unwrap();
			showToast(
				'Retry Started',
				`"${retryDocument.name}" is now being reprocessed.`,
				'success',
			);
			setShowRetryModal(false);
			setRetryDocument(null);
		} catch (error: any) {
			const errorMessage =
				error?.data?.message ||
				error?.message ||
				'Failed to retry statement. Please try again.';
			showToast('Retry Failed', errorMessage, 'error');
		}
	};

	const handleCloseConfigModal = () => {
		setShowConfigSetupModal(false);
	};

	const handleCloseNoConfigModal = () => {
		setShowNoConfigModal(false);
	};

	const handleUploadSuccess = (
		uploadedFileId?: string,
		merchantRecordId?: string,
	) => {
		setShowUploadModal(false);
		navigate(
			merchantRecordId
				? `/merchant-records/${merchantRecordId}`
				: `/analysis/${uploadedFileId}`,
		);
	};

	return (
		<div className="flex min-h-0 flex-col">
			<PageHeader
				title="Statements"
				subtitle="Every statement processed, with its analysis and where it stands. Every file is checked first."
				variant="title"
				actions={
					<Button
						onClick={handleUploadClick}
						variant="primary"
						icon={Plus}
						aria-label="Upload Statement"
					>
						Upload Statement
					</Button>
				}
			/>

			<PageContent className="flex-1">
				<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
					<div className="flex items-center justify-between gap-4 px-6 pt-6">
						<div className="min-w-0 flex-1">
							<div className="text-lg font-normal leading-normal text-gray-900">
								{pageTitle ?? 'All statements'}
							</div>
							{pageSubtitle && (
								<div className="mt-0.5 text-xs text-gray-500">
									{pageSubtitle}
								</div>
							)}
						</div>
						<div className="flex shrink-0 items-center gap-3">
							<div className="relative w-64 shrink-0">
								<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
								<input
									type="text"
									aria-label="Search documents"
									placeholder="Search documents..."
									value={searchInput}
									onChange={(e) => handleSearchChange(e.target.value)}
									className={`${INPUT} w-full bg-gray-50 border-gray-200 !pl-9`}
								/>
							</div>

							<select
								aria-label="Partner"
								className={`${INPUT} w-[200px] shrink-0`}
								value={partnerId}
								onChange={(e) => setPartnerId(e.target.value)}
							>
								<option value="">All partners</option>
								{partners.map(([id, name]) => (
									<option key={id} value={id}>
										{name}
									</option>
								))}
							</select>

							{/* Manual refresh — replaces the removed 10s auto-poll */}
							<Button
								variant="outline"
								size="md"
								icon={RefreshCw}
								onClick={() => refetch()}
								loading={isFetching}
								aria-label="Refresh list"
								className={SECONDARY}
							>
								Refresh
							</Button>

							<FilterDropdown
								placeholder="Filter"
								variant="outline"
								size="md"
								buttonClassName={`${SECONDARY} !gap-0`}
								groups={
									[
										...(isAdmin
											? [
													{
														id: 'uploader',
														title: 'Uploader',
														options: uploaderOptions,
														selectedValues: selectedUploaders,
														onSelectionChange: handleUploaderFilterChange,
														showSearch: true,
														searchPlaceholder: 'Search users...',
													},
												]
											: []),
										{
											id: 'profit',
											title: 'Profit Status',
											options: profitOptions,
											selectedValues: selectedProfitStatus,
											onSelectionChange: handleProfitFilterChange,
										},
									] as FilterGroup[]
								}
							/>
						</div>
					</div>

					<div
						className="flex flex-wrap gap-2 px-6 pt-4"
						role="group"
						aria-label="Status"
					>
						{STATUS_CHIPS.map((c) => (
							<button
								key={c.id}
								type="button"
								aria-pressed={chip === c.id}
								onClick={() => setChip(c.id)}
								className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
									chip === c.id
										? 'border-primary-500 bg-primary-500 text-white'
										: 'border-gray-200 bg-white text-gray-600'
								}`}
							>
								{c.label}
								<span className="ml-1.5 opacity-75">{chipCounts[c.id]}</span>
							</button>
						))}
					</div>

					<div className="mt-4 w-full">
						{documents.length === 0 &&
						!(isFetching || isLoading) &&
						!searchInput &&
						!selectedUploaders.length &&
						!selectedProfitStatus.length &&
						chip === 'all' &&
						!partnerId ? (
							<div className="flex w-full flex-col items-center justify-center py-16">
								<FileText className="mx-auto mb-4 h-16 w-16 text-gray-400" />
								<Typography variant="h3" className="mb-2 text-gray-600">
									No documents uploaded yet
								</Typography>
								<Typography variant="body1" className="text-gray-500">
									Upload your first financial statement to get started with
									analysis
								</Typography>
							</div>
						) : documents.length === 0 && !(isFetching || isLoading) ? (
							<div className="p-10 text-center text-sm text-gray-500">
								No statements match. Clear the search or the filter.
							</div>
						) : isFetching ? (
							<SkeletonTable rows={10} columns={7} showHeader={true} />
						) : (
							<table className="w-full table-auto border-collapse">
								<thead>
									<tr>
										<th className={`${TH} w-[28%]`}>Document</th>
										<th className={TH}>Rep · partner</th>
										<th className={TH}>Type</th>
										<th className={TH}>Months</th>
										<th className={TH}>Date</th>
										<th className={TH}>Status</th>
										<th className={TH}>Actions</th>
									</tr>
								</thead>
								<tbody>
									{documents.map((doc) => (
										<tr
											key={doc.id}
											className="cursor-pointer border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
											onClick={() => navigate(documentPath(doc))}
										>
											<td className={`${TD} min-w-[220px]`}>
												<div className="flex items-center gap-3">
													<span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-600">
														<File className="h-5 w-5" />
													</span>
													<div className="min-w-0">
														<div className="break-words font-medium text-gray-900">
															{doc.name}
														</div>
														<div className="break-words text-gray-500">
															{doc.groupLabel ?? doc.size}
														</div>
													</div>
												</div>
											</td>
											<td className={TD}>
												<div className="text-gray-900">
													{doc.uploaded_by?.name || 'Unknown'}
												</div>
												<div className="break-words text-gray-500">
													{doc.partner?.name ?? '—'}
												</div>
											</td>
											<td className={TD}>
												{doc.selected_rate_types &&
												doc.selected_rate_types.length > 0
													? formatRatePlanTypes(
															doc.selected_rate_types,
															doc.name,
														)
													: formatRatePlanType(doc.rate_plan_type, doc.name)}
											</td>
											<td className={TD}>{doc.processingMonth}</td>
											<td className={`${TD} whitespace-nowrap text-gray-500`}>
												{doc.date}
											</td>
											<td className={TD}>
												<StatusChip status={doc.v9Status} />
												{doc.v9Status.warning && (
													<div className="mt-1 max-w-[46ch] text-xs text-gray-600">
														{doc.v9Status.warning}
													</div>
												)}
											</td>
											<td className={`${TD} whitespace-nowrap`}>
												<div className="flex items-center gap-2">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															navigate(documentPath(doc));
														}}
														className={BTN_SM}
													>
														<Eye className="h-3 w-3" />
														{doc.v9Status.group === 'ready'
															? 'View Analysis'
															: 'Open'}
													</button>
													{canDeleteDocument(doc) && (
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																handleDeleteClick(doc);
															}}
															disabled={isDeleting}
															className={`${BTN_SM} text-red-700`}
															title="Delete file"
															aria-label="Delete file"
														>
															<Trash2 className="h-3 w-3" />
														</button>
													)}
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>

					{/* Pagination Controls */}
					{statements?.pagination && statements.pagination.total_pages > 1 && (
						<div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
							<div className="text-sm text-gray-600">
								Page {statements.pagination.page} of{' '}
								{statements.pagination.total_pages}
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									className={BTN_SM}
									onClick={() =>
										handlePageChange(statements.pagination.page - 1)
									}
									disabled={statements.pagination.page <= 1}
								>
									← Previous
								</button>
								<button
									type="button"
									className={BTN_SM}
									onClick={() =>
										handlePageChange(statements.pagination.page + 1)
									}
									disabled={
										statements.pagination.page >=
										statements.pagination.total_pages
									}
								>
									Next →
								</button>
							</div>
						</div>
					)}
				</div>
			</PageContent>

			{/* No Credits Modal */}
			<Modal
				isOpen={showNoCreditsModal}
				onClose={() => setShowNoCreditsModal(false)}
				size="md"
				title="No Uploads Left"
			>
				<ModalBody>
					<Alert variant="error" className="mb-4">
						You don't have any uploads remaining. Purchase more credits to
						continue analyzing statements.
					</Alert>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={() => setShowNoCreditsModal(false)}
					>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleGoToSubscription}>
						Purchase Credits
					</Button>
				</ModalFooter>
			</Modal>

			{/* Config Setup Modal */}
			<ConfigSetupModal
				isOpen={showConfigSetupModal}
				onClose={handleCloseConfigModal}
				showViewPlans={true}
			/>

			{/* No Config Modal */}
			<NoConfigModal
				isOpen={showNoConfigModal}
				onClose={handleCloseNoConfigModal}
			/>

			{showUploadModal && (
				<StatementUpload
					onClose={() => setShowUploadModal(false)}
					onSuccess={handleUploadSuccess}
				/>
			)}

			{/* Retry Confirmation Modal */}
			<Modal
				isOpen={showRetryModal && !!retryDocument}
				onClose={() => {
					setShowRetryModal(false);
					setRetryDocument(null);
				}}
				size="md"
			>
				<ModalHeader>
					<div className="flex items-center gap-3">
						<div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
							<RotateCcw className="h-6 w-6 text-orange-600" />
						</div>
						<div>
							<Typography variant="h3">Retry Statement Analysis</Typography>
						</div>
					</div>
				</ModalHeader>
				<ModalBody>
					<Typography variant="body1" className="text-gray-600">
						Are you sure you want to retry "{retryDocument?.name}"? This will
						restart the analysis process.
					</Typography>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={() => {
							setShowRetryModal(false);
							setRetryDocument(null);
						}}
					>
						Cancel
					</Button>
					<Button variant="primary" onClick={confirmRetry} loading={retrying}>
						Retry Analysis
					</Button>
				</ModalFooter>
			</Modal>

			{/* Delete Confirmation Modal */}
			<DeleteConfirmationModal
				isOpen={showDeleteModal}
				document={documentToDelete}
				isDeleting={isDeleting}
				errorMessage={deleteError}
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
			/>

			{/* Customize Fee Structure Modal */}
			{showCustomizeModal && (
				<CustomizeFeeStructureModal
					isOpen={showCustomizeModal}
					onClose={handleCustomizeClose}
					onConfirm={handleCustomizeConfirm}
					configId={customizeConfigId}
					fileId={customizeDocument?.id || ''}
				/>
			)}
		</div>
	);
};

export default StatementAnalyzer;
